'use client'

import { MutationStatus } from '@tanstack/react-query'
import { clsx } from 'clsx'
import * as React from 'react'
import { useGsapContext } from '~/hooks/use-gsap-context'
import { gsap } from '~/lib/gsap'

interface LoadingIconProps {
  status: MutationStatus
  className?: string
}

const LoadingIcon: React.FC<LoadingIconProps> = ({ status, className }) => {
  const arrowTlRef = React.useRef(gsap.timeline({ paused: true }))
  const loadingTlRef = React.useRef(gsap.timeline({ paused: true, repeat: -1 }))
  const errorTlRef = React.useRef(gsap.timeline({ paused: true }))
  const successTlRef = React.useRef(gsap.timeline({ paused: true }))
  const hasMountedRef = React.useRef(false)

  const svgRef = React.useRef<SVGSVGElement>(null)

  const [deferredStatus, setDeferredStatus] = React.useState(status)
  const [canSyncStatus, setCanSyncStatus] = React.useState(true)

  useGsapContext(() => {
    const arrowPaths = svgRef.current?.querySelectorAll('.arrow-line')
    const circlePath = svgRef.current?.querySelector('.circle-line')
    const xPaths = svgRef.current?.querySelectorAll('.x-line')
    const checkPath = svgRef.current?.querySelector('.check-line')

    arrowTlRef.current.fromTo(
      [arrowPaths],
      {
        drawSVG: '100% 0',
        stroke: '#FFF',
      },
      { drawSVG: '0 0', stroke: 'currentColor', duration: 0.5 }
    )

    loadingTlRef.current
      .set([circlePath], { display: 'block' })
      .fromTo(
        [circlePath],
        { drawSVG: '0 0' },
        {
          duration: 1.2,
          drawSVG: '0% 80%',
          ease: 'power1.out',
        }
      )
      .fromTo(
        [circlePath],
        { drawSVG: '0 80%' },
        {
          duration: 0.8,
          drawSVG: '100% 100%',
          ease: 'power1.in',
        }
      )
      .fromTo(
        [circlePath],
        {
          rotation: 0,
          transformOrigin: '50% 50%',
        },
        {
          duration: 2,
          rotation: 360,
          transformOrigin: '50% 50%',
          ease: 'linear',
        },
        0
      )

    errorTlRef.current
      .fromTo(
        [xPaths],
        { drawSVG: '0 0' },
        { drawSVG: '100% 0', duration: 0.3 }
      )
      .fromTo(
        [xPaths],
        { stroke: '#FFF' },
        { stroke: '#FF4949', duration: 1.5 }
      )
      .to([xPaths], {
        drawSVG: '0 0',
        stroke: '#FFF',
        ease: 'power1.out',
        duration: 0.4,
      })

    successTlRef.current
      .fromTo(
        [checkPath],
        {
          drawSVG: '0 0',
        },
        { drawSVG: '100% 0', duration: 0.3 }
      )
      .fromTo(
        [checkPath],
        { stroke: '#FFF' },
        { stroke: '#2CFFD1', duration: 1.5 }
      )
      .to([checkPath], {
        drawSVG: '0 0',
        stroke: '#FFF',
        ease: 'power1.out',
        duration: 0.4,
      })
  }, [])

  React.useEffect(() => {
    switch (deferredStatus) {
      case 'loading':
        if (!hasMountedRef.current) {
          // instant
          arrowTlRef.current.seek('end')
          loadingTlRef.current.play()
          setCanSyncStatus(true)
        } else {
          // transition
          setCanSyncStatus(false)
          arrowTlRef.current.play().eventCallback('onComplete', () => {
            loadingTlRef.current.play()
            setCanSyncStatus(true)
          })
        }
        break
      case 'error':
        setCanSyncStatus(false)
        loadingTlRef.current.seek(0).pause()
        errorTlRef.current.restart().eventCallback('onComplete', () => {
          arrowTlRef.current.reverse()
          setCanSyncStatus(true)
        })
        break
      case 'success':
        setCanSyncStatus(false)
        loadingTlRef.current.seek(0).pause()
        successTlRef.current.restart().eventCallback('onComplete', () => {
          arrowTlRef.current.reverse()
          setCanSyncStatus(true)
        })
        break
      default:
        setCanSyncStatus(true)
        break
    }
    hasMountedRef.current = true
  }, [deferredStatus])

  React.useEffect(() => {
    if (canSyncStatus) {
      setDeferredStatus(status)
    }
  }, [status, canSyncStatus])

  return (
    <div className={clsx('text-dark-control-base', className)}>
      <svg
        ref={svgRef}
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          display="none"
          className="circle-line"
          cx="7.5"
          cy="7.5"
          r="5.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          className="arrow-line"
          d="M12.5 7.5L2.5 7.5"
          stroke="#FFF"
          strokeLinecap="round"
        />
        <path
          className="arrow-line"
          d="M12.5 7.5L8.5 11.5"
          stroke="#FFF"
          strokeLinecap="round"
        />
        <path
          className="arrow-line"
          d="M12.5 7.5L8.5 3.5"
          stroke="#FFF"
          strokeLinecap="round"
        />
        <g id="x-mark">
          <path
            className="x-line"
            d="M11.5 1.5L6.5 6.5"
            stroke="#FFF"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            className="x-line"
            d="M6.5 6.5L1.5 11.5"
            stroke="#FFF"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            className="x-line"
            d="M11.5 11.5L6.5 6.5"
            stroke="#FFF"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            className="x-line"
            d="M6.5 6.5L1.5 1.5"
            stroke="#FFF"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </g>
        <path
          className="check-line"
          d="M4.13558 8.24192L6.88246 10.7492L11.1624 4.23346"
          stroke="#FFF"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  )
}

export default LoadingIcon
